import React from "react";

/* Signature motif: an ECG pulse line (clinical data, in Core)
   resolving into a QR grid (a patient action, in the App).
   Colors: green pulse → blue finder cells + paper data cells, on ink. */

// 8×8 QR-like matrix. Solid 3×3 "finder" blocks in three corners; data in between.
const QR = [
  [1, 1, 1, 0, 0, 1, 1, 1],
  [1, 1, 1, 0, 1, 1, 1, 1],
  [1, 1, 1, 0, 0, 1, 1, 1],
  [0, 1, 0, 1, 1, 0, 1, 0],
  [1, 0, 1, 0, 1, 1, 0, 1],
  [1, 1, 1, 0, 1, 0, 1, 1],
  [1, 1, 1, 1, 0, 1, 0, 1],
  [1, 1, 1, 0, 1, 1, 0, 0],
];

const isFinder = (r, c) => (r < 3 && c < 3) || (r < 3 && c > 4) || (r > 4 && c < 3);

// ECG heartbeat along a 130 baseline, then a short lead-in of dots toward the grid.
const PULSE = "M8,130 H62 l8,0 l12,-16 l12,42 l14,-96 l14,150 l12,-80 l10,0 H210";

export default function PulseQR({ className = "" }) {
  const cell = 18;
  const step = 20;
  const x0 = 320;
  const y0 = 40;

  return (
    <svg
      viewBox="0 0 500 220"
      className={className}
      role="img"
      aria-label="An ECG pulse line resolving into a QR code — data from Core becoming an action in the patient App."
    >
      <defs>
        <linearGradient id="pulseStroke" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#3F9E72" />
          <stop offset="100%" stopColor="#63BE99" />
        </linearGradient>
      </defs>

      {/* baseline guide */}
      <line x1="8" y1="130" x2="210" y2="130" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />

      {/* ECG pulse */}
      <path className="pulse-path" d={PULSE} stroke="url(#pulseStroke)" strokeWidth="2.4" />

      {/* glow at the QRS peak */}
      <circle cx="122" cy="34" r="4" fill="#63BE99" className="pulse-blip">
        <animate attributeName="r" values="3;5;3" dur="2.6s" repeatCount="indefinite" />
      </circle>

      {/* lead-in dots: pulse dissolving toward the grid */}
      {[240, 262, 284, 304].map((cx, i) => (
        <circle
          key={cx}
          cx={cx}
          cy="130"
          r={3 - i * 0.4}
          fill="#3F9E72"
          className="qr-cell"
          style={{ animationDelay: `${1.0 + i * 0.12}s` }}
        />
      ))}

      {/* QR grid */}
      {QR.map((row, r) =>
        row.map((v, c) =>
          v ? (
            <rect
              key={`${r}-${c}`}
              x={x0 + c * step}
              y={y0 + r * step}
              width={cell}
              height={cell}
              rx="3"
              fill={isFinder(r, c) ? "#1C6EB4" : "rgba(245,247,243,0.94)"}
              className="qr-cell"
              style={{ animationDelay: `${1.5 + (r + c) * 0.035}s` }}
            />
          ) : null
        )
      )}
    </svg>
  );
}

/* Slim horizontal divider variant of the motif — a pulse that ends in a few QR cells. */
export function PulseDivider({ className = "" }) {
  return (
    <svg
      viewBox="0 0 1200 40"
      preserveAspectRatio="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M0,20 H520 l10,0 l10,-12 l10,26 l12,-40 l12,54 l10,-28 l8,0 H900"
        fill="none"
        stroke="rgba(63,158,114,0.5)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <rect
          key={i}
          x={920 + i * 16}
          y={12}
          width="10"
          height="10"
          rx="2"
          fill={i % 3 === 0 ? "rgba(28,110,180,0.7)" : "rgba(15,42,71,0.28)"}
        />
      ))}
    </svg>
  );
}
